import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getProductosDestacados } from '../lib/portalClienteApi'

/**
 * Carrusel COMPACTO — pensado para embeber dentro del card "La Tienda Limpack"
 * en el grid i-secundarios-grid de /cliente/Inicio.jsx.
 *
 * Sin container dark glass propio ni header ni CTA — todo eso lo pone el card padre.
 * Este componente aporta solo: viewport + track + 1 slide (con 1 card compacta) + dots + swipe + auto-play.
 *
 * - Auto-play cada 3800ms, pause on hover/touch (fiel a la landing 302-329, 1221-1251).
 * - Swipe touch con dx > 40px.
 * - prefers-reduced-motion: sin auto-play.
 * - Card clickeable → /catalogo?destacado={sku}.
 */
export default function ProductosDestacadosCarrusel() {
  const [items, setItems] = useState(null)  // null = loading, [] = sin resultados
  const [slideActivo, setSlideActivo] = useState(0)
  const viewportRef = useRef(null)
  const autoplayRef = useRef(null)
  const touchStartRef = useRef(null)

  // Fetch al mount
  useEffect(() => {
    let cancelado = false
    getProductosDestacados(6).then((data) => {
      if (!cancelado) setItems(data)
    }).catch(() => { if (!cancelado) setItems([]) })
    return () => { cancelado = true }
  }, [])

  // 1 producto por slide (compacto embebido).
  const slides = useMemo(() => items || [], [items])
  const totalSlides = slides.length

  useEffect(() => {
    if (slideActivo >= totalSlides && totalSlides > 0) setSlideActivo(0)
  }, [totalSlides, slideActivo])

  // Auto-play 3800ms + hover/touch handlers.
  useEffect(() => {
    if (totalSlides < 2) return
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) return

    const arrancar = () => {
      detener()
      autoplayRef.current = setInterval(() => {
        setSlideActivo((s) => (s + 1) % totalSlides)
      }, 3800)
    }
    const detener = () => {
      if (autoplayRef.current) { clearInterval(autoplayRef.current); autoplayRef.current = null }
    }

    arrancar()
    const el = viewportRef.current
    if (!el) return () => detener()

    const onEnter = () => detener()
    const onLeave = () => arrancar()
    const onTouchStart = (e) => { touchStartRef.current = e.touches[0].clientX; detener() }
    const onTouchEnd = (e) => {
      if (touchStartRef.current == null) return
      const dx = e.changedTouches[0].clientX - touchStartRef.current
      if (Math.abs(dx) > 40) {
        setSlideActivo((s) => ((s + (dx < 0 ? 1 : -1)) % totalSlides + totalSlides) % totalSlides)
      }
      touchStartRef.current = null
      arrancar()
    }
    el.addEventListener('mouseenter', onEnter)
    el.addEventListener('mouseleave', onLeave)
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchend', onTouchEnd)

    return () => {
      detener()
      el.removeEventListener('mouseenter', onEnter)
      el.removeEventListener('mouseleave', onLeave)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [totalSlides])

  if (items === null) {
    return <div className="tc-skeleton"><CarruselStyles /></div>
  }
  if (items.length === 0) return null

  return (
    <div className="tc-embed">
      <div className="tc-viewport" ref={viewportRef}>
        <div
          className="tc-track"
          style={{ transform: `translateX(-${slideActivo * 100}%)` }}
        >
          {slides.map((p) => {
            const precio = Number(p.precio_publico || 0)
            const mercado = Number(p.precio_mercado || 0)
            const ahorroPct = (mercado > precio && precio > 0)
              ? Math.round((1 - precio / mercado) * 100)
              : 0
            return (
              <Link
                key={p.sku}
                to={`/catalogo?destacado=${p.sku}`}
                className="tc-card"
              >
                <div className="tc-img-wrap">
                  {ahorroPct > 0 && <span className="tc-saving">−{ahorroPct}%</span>}
                  <img src={p.imagen_url} alt={p.nombre} loading="lazy" />
                </div>
                <div className="tc-info">
                  <div className="tc-name">{p.nombre}</div>
                  <div className="tc-pres">{p.presentacion || ''}</div>
                  <div className="tc-price-row">
                    <span className="tc-price">${precio.toLocaleString('es-UY')}</span>
                    {mercado > precio && (
                      <span className="tc-pmercado">${mercado.toLocaleString('es-UY')}</span>
                    )}
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      {totalSlides > 1 && (
        <div className="tc-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`tc-dot ${i === slideActivo ? 'on' : ''}`}
              onClick={() => setSlideActivo(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      <CarruselStyles />
    </div>
  )
}

/**
 * Estilos del carrusel compacto embebido. Namespace `tc-*` para no chocar.
 * Adaptación de deploy_v5/index.html líneas 302-329 al modo embed sin container.
 */
function CarruselStyles() {
  return (
    <style>{`
      .tc-embed {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
      }

      .tc-viewport {
        position: relative;
        overflow: hidden;
        border-radius: 12px;
      }
      .tc-track {
        display: flex;
        transition: transform .6s cubic-bezier(.65,0,.35,1);
      }

      .tc-card {
        flex: 0 0 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        background: rgba(255,255,255,.96);
        border-radius: 12px;
        padding: 14px 16px;
        text-decoration: none;
        color: inherit;
        transition: transform .25s, background .2s;
        min-height: 130px;
        box-sizing: border-box;
      }
      .tc-card:hover { transform: translateY(-1px); background: #fff }
      .tc-img-wrap {
        flex: 0 0 100px;
        width: 100px; height: 100px;
        background: #fff;
        border-radius: 10px;
        overflow: hidden;
        display: flex; align-items: center; justify-content: center;
        position: relative;
      }
      @media (max-width: 640px) {
        .tc-img-wrap { flex: 0 0 92px; width: 92px; height: 92px; }
        .tc-card { min-height: 120px; padding: 12px 14px; gap: 10px; }
      }
      .tc-img-wrap img {
        width: 100%; height: 100%;
        object-fit: cover;
        display: block;
      }
      .tc-saving {
        position: absolute; top: 6px; right: 6px;
        background: #00A8B5; color: #fff;
        font-size: 13px; font-weight: 800;
        padding: 4px 9px; border-radius: 999px;
        letter-spacing: .02em;
        box-shadow: 0 3px 10px rgba(0,168,181,.45);
        line-height: 1;
      }
      @media (max-width: 640px) {
        .tc-saving { font-size: 12px; padding: 4px 8px; }
      }
      .tc-info {
        flex: 1; min-width: 0;
        display: flex; flex-direction: column;
        gap: 4px;
      }
      .tc-name {
        font-size: 13px; color: #1A3461; font-weight: 700;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      @media (max-width: 640px) {
        /* En mobile el card es más ancho relativo al thumb → cada línea entra más
           texto y 2 líneas alcanzan para la mayoría de los nombres del catálogo.
           Nombres extra largos (>60 chars) se cortan por diseño; ver GU12 abajo. */
        .tc-name { -webkit-line-clamp: 2; line-clamp: 2; }
      }
      .tc-pres {
        font-size: 11px; color: #7888A3;
        font-weight: 600;
      }
      .tc-price-row {
        display: flex; align-items: baseline; gap: 8px;
        margin-top: 5px;
      }
      .tc-price {
        font-size: 22px; color: #00A8B5; font-weight: 900; letter-spacing: -.02em;
        line-height: 1;
      }
      @media (max-width: 640px) {
        .tc-price { font-size: 21px; }
      }
      .tc-pmercado {
        font-size: 13px; color: #7888A3;
        text-decoration: line-through;
        text-decoration-thickness: 1.4px;
      }

      .tc-dots {
        display: flex; justify-content: center; gap: 5px;
        padding: 2px 0;
      }
      .tc-dot {
        width: 5px; height: 5px;
        border-radius: 50%;
        background: rgba(255,255,255,.22);
        border: 0; cursor: pointer;
        transition: all .25s;
        padding: 0;
      }
      .tc-dot.on {
        background: #00A8B5;
        width: 16px;
        border-radius: 999px;
      }

      .tc-skeleton {
        height: 130px;
        border-radius: 12px;
        background: linear-gradient(90deg, rgba(255,255,255,.05), rgba(255,255,255,.12), rgba(255,255,255,.05));
        background-size: 200% 100%;
        animation: tc-shimmer 1.4s infinite;
      }
      @media (max-width: 640px) {
        .tc-skeleton { height: 120px; }
      }
      @keyframes tc-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  )
}
